import APIConfig from "service/network/APIConfig";
import APIService from "service/network/APIService";

/**
 * Diagnosis v2 API client (api-diagnosis-v2-service).
 *
 * Mirrors ChiefComplaintsService — same request/response shape rules, same
 * optimistic-lock contract:
 *
 * 1. Every mutating call needs `expectedVersion` (the last version the caller
 *    saw). On mismatch the server returns 409 with a fresh doc so the caller
 *    can reconcile. Pass `0` for the first-ever write against a new patient.
 *
 * 2. Actor is derived server-side from the auth headers phr already sends.
 *    Request bodies deliberately do NOT carry an actor.
 *
 * 3. `complaintIds` are real references to CC v2 entries. `medicineRefs` and
 *    `labRefs` are placeholder empty arrays until MD v2 / LI v2 land.
 */
class DiagnosisService {
  constructor() {
    this.apiService = new APIService();
  }

  list(patientId, onResponse, onError) {
    const url = APIConfig.diagnosisV2.list.format(patientId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Add a new diagnosis (or reactivate a resolved one when the picked ICD
   * matches an existing entry — server enforces the dedup).
   *
   * Body shape must match beans.AddRequest in api-diagnosis-v2-service:
   *   { diagnosis, diagnosisCode?, ongoing, sinceWhen, description?,
   *     consultedDoctor, complaintIds?, medicineRefs?, labRefs?,
   *     expectedVersion }
   */
  add(patientId, body, onResponse, onError) {
    const url = APIConfig.diagnosisV2.add.format(patientId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Update a diagnosis's status. `ongoing: false` explicitly resolves it —
   * different from CC v2 which auto-resolves on VAS=0. Setting status on an
   * already-resolved diagnosis is rejected 409; the client must call `add`
   * again with the same ICD to reopen (that path preserves the diagnosisId
   * and trend history).
   */
  updateStatus(patientId, diagnosisId, body, onResponse, onError) {
    const url = APIConfig.diagnosisV2.updateStatus.format(patientId, diagnosisId);
    return this.apiService.post(url, body, onResponse, onError);
  }

  /**
   * Soft-delete a diagnosis. Body carries `expectedVersion` so we still
   * honour optimistic locking on a DELETE (there is no query-string variant
   * on the server).
   */
  delete(patientId, diagnosisId, expectedVersion, onResponse, onError) {
    const url = APIConfig.diagnosisV2.delete.format(patientId, diagnosisId);
    // Fetch directly — the shared apiService.delete has no body support.
    return fetch(url, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        client: this._client(),
        phone_no: this._header("phone_no"),
        auth_token: this._header("auth_token"),
      },
      body: JSON.stringify({ expectedVersion: expectedVersion || 0 }),
    })
      .then((r) => r.json().then((data) => (r.ok ? onResponse(data) : onError(data))))
      .catch((err) =>
        onError({ message: err && err.message ? err.message : "Delete failed" })
      );
  }

  trend(patientId, diagnosisId, onResponse, onError) {
    const url = APIConfig.diagnosisV2.trend.format(patientId, diagnosisId);
    return this.apiService.get(url, onResponse, onError);
  }

  /**
   * Mark one history row as incorrect. Audit-only — the live Diagnosis
   * record is untouched (values on the row stay so the audit table can
   * still show what was originally recorded). Body: { expectedVersion }.
   */
  markHistoryIncorrect(patientId, diagnosisId, index, expectedVersion, onResponse, onError) {
    const url = APIConfig.diagnosisV2.markHistoryIncorrect.format(
      patientId,
      diagnosisId,
      index
    );
    return this.apiService.post(
      url,
      { expectedVersion: expectedVersion || 0 },
      onResponse,
      onError
    );
  }

  /**
   * Correct one history row — overwrites its display fields with the
   * supplied values; server captures the pre-correction snapshot into
   * StatusEntry.correction.previousSnapshot. Body:
   *   { ongoing, sinceWhen, toWhen?, description?, consultedDoctor,
   *     complaintIds?, medicineRefs?, labRefs?, expectedVersion }
   */
  correctHistory(patientId, diagnosisId, index, body, onResponse, onError) {
    const url = APIConfig.diagnosisV2.correctHistory.format(
      patientId,
      diagnosisId,
      index
    );
    return this.apiService.post(url, body, onResponse, onError);
  }

  _header(name) {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      if (name === "phone_no") return (user && user.phoneno) || "";
      if (name === "auth_token") {
        return (user && user.accessToken && user.accessToken.accessToken) || "";
      }
    } catch (e) {
      /* localStorage unavailable — return empty and let the server 401 */
    }
    return "";
  }

  _client() {
    try {
      const user = JSON.parse(localStorage.getItem("zy.USER_PROFILE"));
      const isDoctor =
        user && user.doctorProfile && user.doctorProfile.id > 0 &&
        !(user.employeeProfile && user.employeeProfile.id > 0);
      return isDoctor ? "doctor_web" : "bridge_web";
    } catch (e) {
      return "bridge_web";
    }
  }
}

export default DiagnosisService;
